import { createIcon } from "../utils/icons";

export type TallySlideDirection = "up" | "down";

const TALLY_ANIM_MS = 280;
const TALLY_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";
const TALLY_MAX = 9999;

const tallyQueue = new WeakMap<HTMLElement, number[]>();

function createTallyDigit(value: number): HTMLSpanElement {
    const digit = document.createElement("span");
    digit.className = "tally-count-digit";
    digit.textContent = String(value);
    return digit;
}

export function ensureTallyStructure(countEl: HTMLElement, value: number): HTMLElement {
    let track = countEl.querySelector<HTMLElement>(".tally-count-track");
    if (track) return track;

    countEl.replaceChildren();
    const slot = document.createElement("span");
    slot.className = "tally-count-slot";
    track = document.createElement("span");
    track.className = "tally-count-track";
    track.appendChild(createTallyDigit(value));
    slot.appendChild(track);
    countEl.appendChild(slot);
    return track;
}

function cancelTallyAnimation(countEl: HTMLElement): void {
    const track = countEl.querySelector<HTMLElement>(".tally-count-track");
    if (!track) return;
    for (const anim of track.getAnimations()) {
        anim.cancel();
    }
    track.querySelectorAll<HTMLElement>(".tally-count-digit").forEach((digit) => {
        for (const anim of digit.getAnimations()) {
            anim.cancel();
        }
    });
    track.style.transform = "";
}

export function commitTallyCount(countEl: HTMLElement, value: number): void {
    cancelTallyAnimation(countEl);
    const track = ensureTallyStructure(countEl, value);
    track.replaceChildren(createTallyDigit(value));
    track.style.transform = "";
    countEl.dataset.value = String(value);
    countEl.classList.remove("tally-count--animating", "tally-count--editing");
    countEl.querySelector(".tally-count-input")?.remove();
}

function processTallyQueue(countEl: HTMLElement): void {
    if (countEl.classList.contains("tally-count--animating")) return;
    const queue = tallyQueue.get(countEl);
    if (!queue || queue.length === 0) return;

    const newValue = queue.shift()!;
    const oldValue = Number(countEl.dataset.value ?? "0");
    if (oldValue === newValue) {
        processTallyQueue(countEl);
        return;
    }

    const direction: TallySlideDirection = newValue > oldValue ? "up" : "down";
    const track = ensureTallyStructure(countEl, oldValue);
    cancelTallyAnimation(countEl);

    const fromDigit = createTallyDigit(oldValue);
    const toDigit = createTallyDigit(newValue);

    countEl.dataset.value = String(newValue);
    countEl.classList.add("tally-count--animating");

    const slot = countEl.querySelector<HTMLElement>(".tally-count-slot");
    const step = slot?.offsetHeight ?? fromDigit.offsetHeight;

    const animOpts = {
        duration: TALLY_ANIM_MS,
        easing: TALLY_EASING,
        fill: "forwards" as const,
    };

    let trackAnim: Animation;
    let animOut: Animation;
    let animIn: Animation;

    if (direction === "up") {
        track.replaceChildren(fromDigit, toDigit);
        track.style.transform = "translateY(0)";
        trackAnim = track.animate(
            [{ transform: "translateY(0)" }, { transform: `translateY(-${step}px)` }],
            animOpts,
        );
    } else {
        track.replaceChildren(toDigit, fromDigit);
        track.style.transform = `translateY(-${step}px)`;
        trackAnim = track.animate(
            [{ transform: `translateY(-${step}px)` }, { transform: "translateY(0)" }],
            animOpts,
        );
    }

    animOut = fromDigit.animate([{ opacity: 1 }, { opacity: 0 }], animOpts);
    animIn = toDigit.animate([{ opacity: 0 }, { opacity: 1 }], animOpts);

    let settled = false;
    const settle = (): void => {
        if (settled) return;
        settled = true;
        trackAnim.cancel();
        animOut.cancel();
        animIn.cancel();
        commitTallyCount(countEl, newValue);
        processTallyQueue(countEl);
    };

    animIn.addEventListener("finish", settle);
    window.setTimeout(settle, TALLY_ANIM_MS + 80);
}

function animateTallyCount(
    countEl: HTMLElement,
    newValue: number,
    _direction: TallySlideDirection,
): void {
    let queue = tallyQueue.get(countEl);
    if (!queue) {
        queue = [];
        tallyQueue.set(countEl, queue);
    }
    queue.push(newValue);
    processTallyQueue(countEl);
}

export function applyTallyCountAnimation(
    countEl: HTMLElement,
    quantity: number,
    direction: TallySlideDirection,
): void {
    countEl.classList.toggle("tally-count--empty", quantity <= 0);
    animateTallyCount(countEl, quantity, direction);
}

export function updateMinusButton(
    minusBtn: HTMLButtonElement,
    itemName: string,
    quantity: number,
    removeLabel = "Remove item",
): void {
    const outOfStock = quantity <= 0;
    minusBtn.className = outOfStock ? "tally-btn minus tally-btn--trash" : "tally-btn minus";
    minusBtn.setAttribute(
        "aria-label",
        outOfStock ? removeLabel.replace("{name}", itemName) : `Decrease ${itemName} quantity`,
    );
    minusBtn.replaceChildren();
    if (outOfStock) {
        minusBtn.appendChild(createIcon("delete", "icon-sm"));
    } else {
        minusBtn.textContent = "−";
    }
}

export function setTallyQuantity(
    rowOrCount: HTMLElement,
    itemName: string,
    quantity: number,
    options: { minusBtn?: HTMLButtonElement; outOfStockRow?: HTMLElement } = {},
): void {
    const outOfStock = quantity <= 0;
    const row = options.outOfStockRow ?? rowOrCount.closest(".manifest-row") ?? undefined;
    row?.classList.toggle("manifest-row--out-of-stock", outOfStock);

    const countEl = rowOrCount.classList.contains("tally-count")
        ? rowOrCount
        : rowOrCount.querySelector<HTMLElement>(".tally-count");
    if (countEl) {
        countEl.classList.toggle("tally-count--empty", outOfStock);
        tallyQueue.delete(countEl);
        commitTallyCount(countEl, quantity);
    }

    const minusBtn =
        options.minusBtn ??
        rowOrCount.querySelector<HTMLButtonElement>(".tally-btn.minus") ??
        row?.querySelector<HTMLButtonElement>(".tally-btn.minus");
    if (minusBtn) {
        updateMinusButton(minusBtn, itemName, quantity);
    }
}

export function applyTallyQuantity(
    row: HTMLElement,
    itemName: string,
    quantity: number,
    direction: TallySlideDirection,
): void {
    const outOfStock = quantity <= 0;
    row.classList.toggle("manifest-row--out-of-stock", outOfStock);

    const countEl = row.querySelector<HTMLElement>(".tally-count");
    if (countEl) {
        countEl.classList.toggle("tally-count--empty", outOfStock);
        animateTallyCount(countEl, quantity, direction);
    }

    const minusBtn = row.querySelector<HTMLButtonElement>(".tally-btn.minus");
    if (minusBtn) {
        updateMinusButton(minusBtn, itemName, quantity);
    }
}

export function clampTallyQuantity(value: number, min: number): number {
    if (!Number.isFinite(value)) return min;
    return Math.max(min, Math.min(TALLY_MAX, Math.floor(value)));
}

export function parseTallyInput(raw: string): number | null {
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    return Number(trimmed);
}

export interface WireEditableTallyOptions {
    min: number;
    getQuantity: () => number;
    onCommit: (quantity: number) => void;
}

export function wireEditableTallyCount(
    countEl: HTMLElement,
    options: WireEditableTallyOptions,
): void {
    countEl.classList.add("tally-count--editable");
    countEl.setAttribute("role", "button");
    countEl.setAttribute("tabindex", "0");
    countEl.setAttribute("aria-label", "Edit quantity");

    const openEditor = (): void => {
        if (countEl.classList.contains("tally-count--animating")) return;
        if (countEl.classList.contains("tally-count--editing")) return;

        const value = options.getQuantity();
        countEl.classList.add("tally-count--editing");

        const input = document.createElement("input");
        input.type = "text";
        input.inputMode = "numeric";
        input.autocomplete = "off";
        input.className = "tally-count-input";
        input.value = String(value);
        input.setAttribute("aria-label", "Quantity");
        countEl.appendChild(input);

        input.focus();
        input.select();

        let closed = false;
        const close = (commit: boolean): void => {
            if (closed) return;
            closed = true;
            const prev = options.getQuantity();
            const parsed = parseTallyInput(input.value);
            countEl.classList.remove("tally-count--editing");
            input.remove();

            if (!commit || parsed === null) {
                commitTallyCount(countEl, prev);
                return;
            }

            const next = clampTallyQuantity(parsed, options.min);
            commitTallyCount(countEl, next);
            if (next !== prev) {
                options.onCommit(next);
            }
        };

        input.addEventListener("blur", () => close(true));
        input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                input.blur();
            }
            if (e.key === "Escape") {
                e.preventDefault();
                close(false);
            }
        });
    };

    countEl.addEventListener("click", (e) => {
        e.stopPropagation();
        openEditor();
    });
    countEl.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEditor();
        }
    });
}
